'use client';

import { useRouter } from 'next/navigation';
import { Suspense, useEffect, type PropsWithChildren, type ReactNode } from 'react';

import { useAccountPage } from '@eliadmin/core/src/components/dlz/account/account-pages';
import { AccountShell } from '@eliadmin/core/src/components/dlz/account/account-shell';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';

/**
 * Decides what the profile URLs show. A guest gets the sign-in page (`signedOut`, rendered on the
 * server) instead of the core sign-in dialog – on the favourites URL the guest page of the
 * favourites. A signed-in customer gets the DLZ account (navigation, page, `advice` box); other
 * profile URLs of the core lead to „Môj profil“. The customer state only exists on the client, so a
 * loader shows until the store is initialised.
 */
export function ProfileGate({
  signedOut,
  advice,
  children,
}: PropsWithChildren<{ signedOut: ReactNode; advice?: ReactNode }>) {
  const router = useRouter();
  const page = useAccountPage();
  const me = useCustomerStore(state => state.me);
  const isCustomerStoreInitialized = useCustomerStore(state => state.isInitialized);
  const guestFavorites = isCustomerStoreInitialized && !me && page?.key === 'favorites';

  useEffect(() => {
    if (guestFavorites) router.replace(ROUTES.FAVORITE_PRODUCTS());
  }, [guestFavorites, router]);

  useEffect(() => {
    if (!isCustomerStoreInitialized || !me || page) return;
    router.replace(ROUTES.PROFILE());
  }, [isCustomerStoreInitialized, me, page, router]);

  if (!isCustomerStoreInitialized || guestFavorites) return <GenericLoader />;
  if (!me) return signedOut;
  if (!page) return null;

  return (
    <>
      <Suspense fallback={<GenericLoader />}>
        <AccountShell>{children}</AccountShell>
      </Suspense>
      {advice}
    </>
  );
}
